import { useMatrixDistributionContext } from '@app/components/products/ProductMatrix/context';
import MeasurementChart from '@app/components/various/Chart';
import { Input } from '@app/components/various/Fields/Input';
import { Help } from '@app/components/various/Help';
import { itemsToChart } from '@app/ducks/helpers';
import { getNumericValue } from '@app/logic/validation';
import { isDefined } from '@app/utils/is';
import { Preset, Product, UnitsDistribution } from '@typings';
import { useController, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

const MAX_VALUE = 99_999;
const INPUT_MAX_LENGTH = String(MAX_VALUE).length;

interface DistributionNameCellProps {
  preset: Preset;
  variant: Product.Standard;
  name: string;
}

interface AdderCellProps {
  id: string;
  name: string;
}

interface TotalPriceCellProps {
  getTotalPrice: (count: number) => string;
}

interface PriceCellProps extends TotalPriceCellProps {
  cellId: string;
}

const getTotalPresetsCount = (formData: UnitsDistribution.QuantitiesFormData) =>
  Object.values(formData).reduce((acc = 0, item) => {
    if (!isDefined(item)) {
      return acc;
    }

    return acc + item;
  }, 0);

export const DistributionNameCell = ({ preset, variant, name }: DistributionNameCellProps) => {
  const { t } = useTranslation(['orders']);

  return (
    <td className="relative flex h-10 items-center gap-1 pl-2">
      <span className="max-w-52 truncate body-1">{preset.name}</span>
      <Help placement="top-end" contentHeader={`${t('orders:size_distribution_for')} ${name} [%]`} color="gray">
        <MeasurementChart table={itemsToChart(preset.items, variant.itemTable)} />
      </Help>
    </td>
  );
};

export const AdderCell = ({ id, name }: AdderCellProps) => {
  const { t } = useTranslation(['orders']);
  const { setIsEnteringDistributionQuantities } = useMatrixDistributionContext();
  const { field } = useController<UnitsDistribution.QuantitiesFormData>({ name: id });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    field.onChange(getNumericValue(value));
  };

  const handleFocus = () => {
    setIsEnteringDistributionQuantities(true);
  };

  const handleBlur = () => {
    field.onBlur();
    setIsEnteringDistributionQuantities(false);
  };

  return (
    <td className="h-10">
      <label aria-label={t('orders:size_distribution_label', { presetName: name })} className="block max-w-20">
        <Input
          ref={field.ref}
          value={field.value === 0 ? '' : field.value}
          size="medium"
          placeholder="0"
          maxLength={INPUT_MAX_LENGTH}
          inputMode="numeric"
          onFocus={handleFocus}
          onBlur={handleBlur}
          onChange={handleChange}
        />
      </label>
    </td>
  );
};

export const PriceCell = ({ getTotalPrice, cellId }: PriceCellProps) => {
  const cellValue = useWatch<UnitsDistribution.QuantitiesFormData>({ name: cellId });

  const presetsPrice = getTotalPrice(cellValue);

  return <td className="h-10 pr-2 text-end body-2">{presetsPrice}</td>;
};

export const TotalDistributionPriceCell = ({ getTotalPrice }: TotalPriceCellProps) => {
  const formData = useWatch<UnitsDistribution.QuantitiesFormData>();
  const totalPresetsCount = getTotalPresetsCount(formData as UnitsDistribution.QuantitiesFormData);
  const totalPresetsPrice = getTotalPrice(totalPresetsCount);

  return <th className="pr-2 text-end body-2">{totalPresetsPrice}</th>;
};

export const TotalDistributionCountCell = () => {
  const formData = useWatch<UnitsDistribution.QuantitiesFormData>();
  const totalPresets = getTotalPresetsCount(formData as UnitsDistribution.QuantitiesFormData);

  return <th className="pl-3 text-start body-2">{totalPresets}</th>;
};
